/**
 * Localization form — country/language switcher for Shopify Markets and external stores.
 *
 * Shopify Markets countries submit the native `{% form 'localization' %}` endpoint.
 * External store countries (`data-country-source="metaobject"`) redirect on submit to
 * `{store_base_url}` or `{store_base_url}/{language-path}` when a language is selected.
 *
 * Language options are pre-rendered in Liquid for both sources. This component toggles
 * visibility per country (`data-country-source`, `data-country-code`) and keeps the
 * content-select UI in sync.
 *
 * DOM contract: nested `content-select` elements with `[data-select-button]`,
 * `[data-select-input]`, and `[role="menuitem"]` options. Dropdown lists are portaled
 * to `document.body` by content-select.js, so list lookup uses `aria-labelledby`.
 */

/**
 * @class LocalizationForm
 * @extends HTMLElement
 * @description Country/language switcher for Shopify Markets and external Kabrita stores.
 * @example <localization-form></localization-form>
 */
class LocalizationForm extends HTMLElement {
  static #selectIds = { country: 'localization-country', language: 'localization-language' };
  static #countrySource = { shopify: 'shopify', metaobject: 'metaobject' };

  #form = this.querySelector('form');
  #countrySelect = this.querySelector(`#${LocalizationForm.#selectIds.country}`);
  #languageSelect = this.querySelector(`#${LocalizationForm.#selectIds.language}`);
  #languageGroup = this.querySelector('#localization-language-group');
  #countryInput = this.#countrySelect?.querySelector('[data-select-input]');

  connectedCallback() {
    if (!this.#form) return;

    this.#form.addEventListener('submit', (event) => this.#handleSubmit(event));

    if (!this.#countrySelect || !this.#countryInput) return;

    this.#countryInput.addEventListener('change', () => this.#handleCountryChange());

    if (this.#languageSelect) {
      this.#handleCountryChange();
    }
  }

  /**
   * @description Re-filters language options when the selected country changes.
   */
  #handleCountryChange() {
    const countryOption = this.#findSelectedMenuItem(this.#countrySelect);
    this.#updateLanguagesForCountry(this.#countryInput?.value, countryOption);
  }

  /**
   * @description Redirects external store countries on submit; Shopify countries use the native form.
   * @param {SubmitEvent} event
   */
  #handleSubmit(event) {
    const countryOption = this.#findSelectedMenuItem(this.#countrySelect);
    if (!this.#isExternalStoreCountry(countryOption)) return;

    event.preventDefault();

    const languagePath = this.#readLanguagePathSegment(this.#countryInput?.value);
    const url = this.#buildExternalStoreUrl(countryOption.dataset.storeUrl ?? '', languagePath);

    if (url) {
      window.location.assign(url);
    }
  }

  /**
   * @description Finds the portaled dropdown list for a content-select element.
   * @param {HTMLElement | null} selectEl
   * @returns {HTMLElement | null}
   */
  #findContentSelectList(selectEl) {
    const button = selectEl?.querySelector('[data-select-button]');
    if (!button?.id) return null;

    return document.querySelector(`[data-select-list][aria-labelledby="${button.id}"]`);
  }

  /**
   * @description Returns the button, hidden input, and portaled list for a content-select.
   * @param {HTMLElement | null} selectEl
   * @returns {{ list: HTMLElement, button: HTMLElement, input: HTMLInputElement } | null}
   */
  #findContentSelectElements(selectEl) {
    const button = selectEl?.querySelector('[data-select-button]');
    const input = selectEl?.querySelector('[data-select-input]');
    const list = this.#findContentSelectList(selectEl);

    if (!list || !button || !input) return null;

    return { list, button, input };
  }

  /**
   * @description Returns the selected menu item for a content-select from its hidden input value.
   * @param {HTMLElement | null} selectEl
   * @returns {HTMLElement | null}
   */
  #findSelectedMenuItem(selectEl) {
    const code = selectEl?.querySelector('[data-select-input]')?.value;
    const list = this.#findContentSelectList(selectEl);
    if (!code || !list) return null;

    return (
      [...list.querySelectorAll('[role="menuitem"]')].find((item) => item.dataset.value === code) ??
      null
    );
  }

  /**
   * @description Returns whether a country menu item redirects to an external store on submit.
   * @param {HTMLElement | null} menuItem
   * @returns {boolean}
   */
  #isExternalStoreCountry(menuItem) {
    return (
      menuItem?.dataset.countrySource === LocalizationForm.#countrySource.metaobject &&
      Boolean(menuItem?.dataset.storeUrl)
    );
  }

  /**
   * @description Returns whether a language menu item belongs to the selected country.
   * @param {HTMLElement} menuItem
   * @param {string} countryCode
   * @param {boolean} isExternalStore
   * @returns {boolean}
   */
  #isLanguageVisibleForCountry(menuItem, countryCode, isExternalStore) {
    if (isExternalStore) {
      return (
        menuItem.dataset.countrySource === LocalizationForm.#countrySource.metaobject &&
        menuItem.dataset.countryCode === countryCode
      );
    }

    return menuItem.dataset.countrySource === LocalizationForm.#countrySource.shopify;
  }

  /**
   * Builds the external store redirect URL with an optional language path segment.
   * @param {string} storeUrl
   * @param {string} [languagePath]
   * @returns {string}
   */
  #buildExternalStoreUrl(storeUrl, languagePath) {
    const base = storeUrl.replace(/\/$/, '');
    const path = (languagePath ?? '').trim().toLowerCase();

    if (!path) return base;

    return `${base}/${path}`;
  }

  /**
   * Shows or hides the language group, filters options for the country, and re-selects.
   * @param {string | undefined} countryCode
   * @param {HTMLElement | null} countryOption
   */
  #updateLanguagesForCountry(countryCode, countryOption) {
    if (!this.#languageSelect || !countryCode) return;

    if (this.#shouldHideLanguageGroup(countryOption)) {
      this.#languageGroup?.classList.toggle('hidden', true);
      this.#resetLanguageSelect();
      return;
    }

    this.#languageGroup?.classList.toggle('hidden', false);
    this.#filterAndSelectLanguage(countryCode, this.#isExternalStoreCountry(countryOption));
  }

  /**
   * Returns whether the language group should be hidden for the selected country.
   * @param {HTMLElement | null} countryOption
   * @returns {boolean}
   */
  #shouldHideLanguageGroup(countryOption) {
    return (
      this.#isExternalStoreCountry(countryOption) && countryOption?.dataset.hasLanguages === 'false'
    );
  }

  /**
   * Filters language menu items for the country and selects the first visible option.
   * @param {string} countryCode
   * @param {boolean} isExternalStore
   */
  #filterAndSelectLanguage(countryCode, isExternalStore) {
    const elements = this.#findContentSelectElements(this.#languageSelect);
    if (!elements) return;

    const options = [...elements.list.querySelectorAll('[role="menuitem"]')];

    options.forEach((option) => {
      option.classList.toggle(
        'hidden',
        !this.#isLanguageVisibleForCountry(option, countryCode, isExternalStore)
      );
    });

    const visible = options.filter((option) => !option.classList.contains('hidden'));
    if (!visible.length) {
      this.#resetLanguageSelect();
      return;
    }

    const selected = visible.find((option) => option.hasAttribute('data-selected')) ?? visible[0];
    this.#selectLanguageOption(selected);
  }

  /**
   * Syncs the language content-select button, input, and list selection state.
   * @param {HTMLElement} menuItem
   */
  #selectLanguageOption(menuItem) {
    const elements = this.#findContentSelectElements(this.#languageSelect);
    if (!elements) return;

    elements.list
      .querySelectorAll('[role="menuitem"]')
      .forEach((option) => option.removeAttribute('data-selected'));

    menuItem.setAttribute('data-selected', '');
    elements.button.innerHTML = menuItem.cloneNode(true).innerHTML;
    elements.input.value = menuItem.dataset.value ?? '';
  }

  /**
   * Clears the language select so redirects use the store base URL only.
   */
  #resetLanguageSelect() {
    const elements = this.#findContentSelectElements(this.#languageSelect);
    if (!elements) return;

    elements.list
      .querySelectorAll('[role="menuitem"]')
      .forEach((option) => option.removeAttribute('data-selected'));
    elements.button.innerHTML = '';
    elements.input.value = '';
  }

  /**
   * Returns the validated language path segment for an external store redirect.
   * @param {string | undefined} countryCode
   * @returns {string}
   */
  #readLanguagePathSegment(countryCode) {
    const { list } = this.#findContentSelectElements(this.#languageSelect) ?? {};
    const selected = list?.querySelector('[role="menuitem"][data-selected]:not(.hidden)');

    if (
      selected?.dataset.countrySource === LocalizationForm.#countrySource.metaobject &&
      selected.dataset.countryCode === countryCode
    ) {
      return selected.dataset.value ?? '';
    }

    return '';
  }
}

customElements.define('localization-form', LocalizationForm);
